import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { useSelector } from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline';
import { getDesignTokens } from '../../theme';
import { RootState } from '../../store';

type ColorMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  mode: ColorMode;
  toggleColorMode: () => void;
  setMode: (mode: ColorMode) => void;
}

export const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleColorMode: () => {},
  setMode: () => {},
});

export const useThemeContext = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [mode, setMode] = useState<ColorMode>(() => {
    // Check localStorage first
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode) {
      return savedDarkMode === 'true' ? 'dark' : 'light';
    }
    // Fall back to user preferences or default
    return user?.preferences?.theme || 'light';
  });
  
  // Determine the actual palette mode based on the selected mode and system preference
  const actualPaletteMode = useMemo<PaletteMode>(() => {
    if (mode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return mode as PaletteMode;
  }, [mode]);
  
  // Generate the theme based on the palette mode
  const theme = useMemo(() => {
    return createTheme(getDesignTokens(actualPaletteMode));
  }, [actualPaletteMode]);
  
  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (mode !== 'system') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setMode('system'); // Trigger a re-render
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode]);
  
  // Sync mode to localStorage whenever it changes
  useEffect(() => {
    if (mode !== 'system') {
      localStorage.setItem('darkMode', String(mode === 'dark'));
    }
  }, [mode]);
  
  // Update mode when user preferences change (only if no localStorage value)
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    if (!savedDarkMode && user?.preferences?.theme) {
      setMode(user.preferences.theme);
    }
  }, [user?.preferences?.theme]);
  
  const themeContextValue = useMemo(() => ({
    mode,
    toggleColorMode: () => {
      setMode((prevMode) => {
        if (prevMode === 'light') return 'dark';
        if (prevMode === 'dark') return 'system';
        return 'light';
      });
    },
    setMode: (newMode: ColorMode) => {
      setMode(newMode);
    },
  }), [mode]);
  
  return (
    <ThemeContext.Provider value={themeContextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 